import { useEffect } from 'react';
import { getTranslationSpace } from '@trip-ux/common';
import { POLICY_ROUTES, AUTH_HEADERS } from '@trip-ux/constants';
import { get } from 'lodash';
import type { NavigateFunction } from 'react-router-dom';

type RoutesShape = {
  TRAVELLER_DETAILS: string;
  [key: string]: string;
};

type Organization = {
  organizationCode: string;
  default?: boolean;
};

type UserProfile = {
  userType?: string;
  organizations?: Organization[];
  organizationCode?: string;
};

type Actions = {
  resetStore: () => void;
  setOrganizationCode: (orgCode: string, translationSpace: string) => Promise<void>;
  clearQuoteForm: () => void;
  getTmsTranslations: (translationSpace: string, options: unknown, lang: string) => void;
  getTmsConfiguration: (translationSpace: string, options: unknown, lang: string) => void;
  getNavContent: () => void;
};

type Config = {
  options?: {
    organizationCode?: string;
    translation?: unknown;
    translationConfig?: unknown;
  };
};

type UseOrgCodeHandlerOptions = {
  currentPath: string;
  routes: RoutesShape;
  effectiveIsAdmin: boolean;
  userProfile: UserProfile;
  actions: Actions;
  config: Config;
  lang: string;
  navigate: NavigateFunction;
};

export const useOrgCodeHandler = (opts: UseOrgCodeHandlerOptions) => {
  const { currentPath, routes, effectiveIsAdmin, userProfile, actions, config, lang, navigate } = opts;

  useEffect(() => {
    const fetchOrgCode = async () => {
      try {
        if (
          currentPath === routes.TRAVELLER_DETAILS &&
          effectiveIsAdmin &&
          userProfile.userType === AUTH_HEADERS.CIAM_ID
        ) {
          const isOrgCodeValid = userProfile.organizations.some(
            (org) => org.organizationCode === userProfile.organizationCode
          );
          if (!isOrgCodeValid) {
            const defaultOrg = userProfile.organizations.find((org) => org.default === true);
            actions.resetStore();
            const translationSpace = getTranslationSpace('');
            actions.setOrganizationCode(defaultOrg.organizationCode, translationSpace).then(() => {
              window.location.reload();
              actions.clearQuoteForm();
              actions.getTmsTranslations(translationSpace, get(config, 'options.translation'), lang);
              actions.getTmsConfiguration(translationSpace, get(config, 'options.translationConfig'), lang);
              navigate(POLICY_ROUTES.QUOTE);
              actions.getNavContent();
            });
          }
        }
      } catch (error) {
        // keep behavior consistent with previous implementation
        // eslint-disable-next-line no-console
        console.error('Error fetching orgCode:', error);
      }
    };
    fetchOrgCode();
    // Intentionally empty deps to preserve previous behavior (ran once)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
};
